// ============================================================================
// Map colour contract (WS9, shipped with WS8 so the primitive is theme-stable
// from its first render).
//
// PALETTE RULE — NON-NEGOTIABLE: the RAG green / amber / red are reserved
// EXCLUSIVELY for the two RAG dimensions. Every categorical palette (type, AM,
// sub, network) uses cool and neutral hues only, so those three colours always
// mean status and nothing else. Token set mirrors the approved demo
// (PROJECTS/tcs-app/artifacts/tcs-app-facility-map-demo-2026-07-31.html).
//
// CW-J Phase 0.5: this file used to declare its own RAG copy
// (#0F7B54 / #C07500 / #C0271C) while tcs.css, tailwind.config.js and
// design-tokens.ts all declared a *different* one (#10B981 / #F59E0B /
// #EF4444) — so a facility showed one red on its dot and another on its pin.
// Measurement settled it in this file's favour: the values below passed WCAG
// 1.4.11 (5.27 / 3.63 / 5.92) and the app-wide triad failed it
// (2.43 / 2.06 / 3.60). Those values are now the app-wide RAG, declared once
// in tcs.css, and RAG_COLORS is re-exported from there.
// ============================================================================

import { RAG_COLORS, token } from '../design-tokens';
import type { MapDimension, MapFacility } from './map-types';

export { RAG_COLORS };
// CW-J Phase 1c: 'no status' is now one token. UNSET was #B4BCC8 = 1.91:1
// against the map surface, failing WCAG 1.4.11 (>= 3.0 for a UI component
// conveying state) while the RAG pins beside it are 3.63-5.92.
// A FUNCTION, not a const: `token()` reads the live CSS variable, and a
// module-scope const would evaluate before Vite has injected the stylesheet in
// dev — resolving to the `var(--st-neutral)` fallback string, which Google Maps
// cannot parse as a fillColor. Every other token consumer is lazy for the same
// reason (see design-tokens.ts).
export const unsetColor = (): string => token('--st-neutral');
export const OFFICE_COLOR = '#8A5AC2';

// CW-J Phase 2: these two were literals, which cannot participate in a theme —
// and on Google's dark tiles the light-mode values are wrong. Now functions for
// the same lazy-evaluation reason as unsetColor above.
//
// mutedColor() is DELIBERATELY low contrast (2.88 on dark tiles): it means "not
// in the current selection", is drawn at fillOpacity 0.45, and carries no state.
// It is de-emphasis, not a status, so 1.4.11 does not apply — the same call the
// light theme already made with #C7CDD6 at 1.3 on white.
//
// closedColor() keeps its own value rather than collapsing onto --st-neutral:
// 'closed facility' is a DIFFERENT concept from 'no RAG set' and the legend
// distinguishes them. It now measures 4.52 on dark tiles (was 2.55 in light,
// which remains an open light-mode item — a legend decision, not a mapping).
export const mutedColor = (): string => token('--map-muted');
export const closedColor = (): string => token('--map-closed');
/** Ring drawn around a SELECTED pin. #16181D measures 1.31 on dark tiles. */
export const pinRingColor = (): string => token('--map-pin-ring');

// Canonical five (WS1) — cool/neutral only.
export const TYPE_COLORS: Record<string, string> = {
  Surgery_Center: '#3B49C4',
  Dialysis: '#0E7490',
  Hospital: '#6D28D9',
  MOB: '#475569',
  Other: '#78716C',
};
export const TYPE_LABELS: Record<string, string> = {
  Surgery_Center: 'Surgery center',
  Dialysis: 'Dialysis',
  Hospital: 'Hospital',
  MOB: 'MOB',
  Other: 'Other',
};

// Cool/neutral pool for dynamically-assigned categories (AMs ~8, top-N
// networks). Never green/amber/red.
export const CATEGORY_POOL = [
  '#3B49C4', '#0E7490', '#6D28D9', '#A21CAF',
  '#1D4ED8', '#475569', '#0F766E', '#7C3AED',
];

// Strong highlight colours for subcontractor select-and-highlight mode.
// Sub mode NEVER attempts one colour per sub (70 active) — selected subs get
// these, everything else mutes to grey.
export const SUB_HIGHLIGHT_POOL = [
  '#3B49C4', '#0E7490', '#A21CAF', '#1D4ED8', '#7C3AED', '#0F766E',
];

// SENTIMENT_COLORS was removed 2026-08-03 with the 'sentiment' map dimension
// and the stored facilities.account_sentiment column it coloured. It keyed on
// Positive/Neutral/Negative/Critical while the stored column actually held
// Frustrated/Happy, so it could never have matched a single row. Sentiment is
// DERIVED from facility_feedback — see
// tcs-app-prompt-retire-account-sentiment-2026-08-03.md.
// green/amber/red remain reserved exclusively for RAG.

export function bucketOf(f: MapFacility, dim: MapDimension): string {
  switch (dim) {
    case 'type':
      return f.facility_type ?? '—';
    case 'am':
      return f.account_manager_name ?? '—';
    case 'sub':
      return f.provider_name ?? '—';
    case 'acctrag':
      return f.account_status_rag ?? '—';
    case 'network':
      return f.account_name ?? '—';
  }
}

/**
 * Build the bucket -> colour assignment for the active dimension over the
 * visible rows. The RAG dimension uses the reserved status colours; type uses
 * the fixed map; other categorical dimensions take colours from the cool pool
 * in descending bucket-size order (top-N; overflow buckets all render neutral).
 */
export function buildPalette(
  rows: MapFacility[],
  dim: MapDimension,
): Map<string, string> {
  const palette = new Map<string, string>();
  if (dim === 'acctrag') {
    // Object.entries (not keys + index): RAG_COLORS is now getter-backed off
    // the CSS variables, and entries() invokes the getters. Indexing a
    // Record<RagStatus, string> with a plain string would also not type-check.
    for (const [k, v] of Object.entries(RAG_COLORS)) palette.set(k, v);
    palette.set('—', unsetColor());
    return palette;
  }
  if (dim === 'type') {
    for (const k of Object.keys(TYPE_COLORS)) palette.set(k, TYPE_COLORS[k]);
    palette.set('—', unsetColor());
    return palette;
  }
  // dynamic categorical: size-ordered top-N from the pool
  const tally = new Map<string, number>();
  for (const f of rows) {
    const b = bucketOf(f, dim);
    tally.set(b, (tally.get(b) ?? 0) + 1);
  }
  const ordered = [...tally.entries()]
    .filter(([k]) => k !== '—')
    .sort((a, b) => b[1] - a[1]);
  ordered.forEach(([k], i) => {
    palette.set(k, i < CATEGORY_POOL.length ? CATEGORY_POOL[i] : unsetColor());
  });
  palette.set('—', unsetColor());
  return palette;
}
